// dependencies
import { useEffect, useRef, useState } from "react";

export interface UseOverlayAnimationOptions {
	/**
	 * Whether the overlay is open or closed
	 */
	isOpen: boolean;
	/**
	 * CSS property name to watch for transition completion.
	 * Different overlays animate different properties.
	 * @default "opacity"
	 */
	transitionProperty?: string | string[];
}

export interface UseOverlayAnimationReturn<T extends HTMLElement = HTMLElement> {
	/**
	 * Whether the overlay should be rendered in the DOM
	 */
	shouldRender: boolean;
	/**
	 * Whether the overlay is currently animating (for CSS classes)
	 */
	isAnimating: boolean;
	/**
	 * Ref for the animated element to attach transition listeners
	 */
	animatedElementRef: React.RefObject<T | null>;
}

/**
 * Hook for managing overlay animation lifecycle.
 *
 * This hook handles the common pattern of:
 * - Mounting the overlay when it opens
 * - Triggering CSS animations after DOM updates
 * - Unmounting after close animations complete
 *
 * ## Features
 * - **Double RAF pattern**: Ensures DOM is ready before triggering CSS transitions
 * - **Transition end detection**: Automatically unmounts after animations complete
 * - **Multiple property support**: Can watch for different CSS properties
 *
 * ## Usage
 * ```tsx
 * const Component = ({ isOpen, onClose }) => {
 *   const { shouldRender, isAnimating, animatedElementRef } = useOverlayAnimation({
 *     isOpen,
 *     transitionProperty: "transform", // For drawers
 *     // transitionProperty: ["opacity", "transform"], // For modals
 *   });
 *
 *   const { portalRef, isRendered } = usePortal({ shouldRender });
 *
 *   return isRendered ? createPortal(
 *     <div className={isAnimating ? "animate-in" : "animate-out"}>
 *       <div ref={animatedElementRef}>Content</div>
 *     </div>,
 *     portalRef.current
 *   ) : null;
 * };
 * ```
 *
 * @param options - Configuration options for the animation
 * @returns Animation state and element ref
 */
export const useOverlayAnimation = <T extends HTMLElement = HTMLElement>({
	isOpen,
	transitionProperty = "opacity",
}: UseOverlayAnimationOptions): UseOverlayAnimationReturn<T> => {
	const animatedElementRef = useRef<T>(null);
	const isCancelledRef = useRef(false);
	const [isAnimating, setIsAnimating] = useState(false);
	const [shouldRender, setShouldRender] = useState(false);

	// Control overlay mount/unmount and animation trigger
	useEffect(() => {
		if (isOpen) {
			setShouldRender(true);
		} else {
			setIsAnimating(false);
		}
	}, [isOpen]);

	// Trigger opening animation after render
	useEffect(() => {
		if (!shouldRender || !isOpen) {
			return;
		}

		isCancelledRef.current = false;

		let rafCheck = 0;
		let rafStart = 0;
		let rafCommit = 0;

		const startAnimation = () => {
			if (isCancelledRef.current) {
				return;
			}

			const element = animatedElementRef.current;
			if (!element) {
				rafCheck = requestAnimationFrame(startAnimation);
				return;
			}

			rafStart = requestAnimationFrame(() => {
				rafCommit = requestAnimationFrame(() => {
					if (!isCancelledRef.current) {
						setIsAnimating(true);
					}
				});
			});
		};

		rafCheck = requestAnimationFrame(startAnimation);

		return () => {
			isCancelledRef.current = true;
			cancelAnimationFrame(rafCheck);
			cancelAnimationFrame(rafStart);
			cancelAnimationFrame(rafCommit);
		};
	}, [shouldRender, isOpen]);

	// Remove overlay from DOM after close animation completes
	useEffect(() => {
		if (!shouldRender) return;
		const element = animatedElementRef.current;
		if (!element) return;

		// Normalize transition properties to array for easier checking
		const transitionProperties = Array.isArray(transitionProperty) ? transitionProperty : [transitionProperty];

		const handleTransitionEnd = (e: TransitionEvent) => {
			// Only respond to transitions on the properties we care about
			if (!transitionProperties.includes(e.propertyName)) return;
			if (!isOpen) {
				setShouldRender(false);
			}
		};

		element.addEventListener("transitionend", handleTransitionEnd);
		return () => {
			element.removeEventListener("transitionend", handleTransitionEnd);
		};
	}, [shouldRender, isOpen, transitionProperty]);

	return {
		shouldRender,
		isAnimating,
		animatedElementRef,
	};
};
